import { useState, useRef } from "react";
import { authClient, useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { sfx } from "@/lib/sounds";
import MentionAutocomplete from "./MentionAutocomplete";
import { Image as ImageIcon, X, Send, BarChart2, HelpCircle, Video, Megaphone, Trophy, Camera } from "lucide-react";

const KIND_OPTIONS = [
  { key: "text", label: "Text", icon: null },
  { key: "image", label: "Image", icon: ImageIcon },
  { key: "video", label: "Video", icon: Video },
  { key: "question", label: "Question", icon: HelpCircle },
  { key: "poll", label: "Poll", icon: BarChart2 },
  { key: "announcement", label: "Announce", icon: Megaphone },
  { key: "clip", label: "Clip", icon: Trophy },
  { key: "screenshot", label: "Screenshot", icon: Camera },
];

// Base64 downscale
function readFileToBase64(file, maxWidth = 1400) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = reject;
    fr.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = fr.result;
    };
    fr.readAsDataURL(file);
  });
}

export default function PostComposer({ onPosted, communityId = "", communityName = "" }) {
  const { user } = useAuth();
  const [kind, setKind] = useState("text");
  const [body, setBody] = useState("");
  const [images, setImages] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [poll, setPoll] = useState({ question: "", options: ["", ""], multi: false });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const textRef = useRef();

  if (!user) {
    return (
      <div className="rounded-2xl glass p-5 text-center text-white/60 text-sm" data-testid="post-composer-locked">
        Sign in to post to the community.
      </div>
    );
  }

  const addImages = async (files) => {
    const arr = Array.from(files || []).slice(0, 4);
    const bases = await Promise.all(arr.map((f) => readFileToBase64(f)));
    setImages((prev) => [...prev, ...bases].slice(0, 4));
    sfx.click();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!body.trim() && images.length === 0 && kind !== "poll") {
      toast.error("Say something or attach an image");
      return;
    }
    setBusy(true);
    try {
      const payload = { kind, body, images, video_url: videoUrl };
      if (communityId) payload.community_id = communityId;
      if (kind === "poll") payload.poll = { question: poll.question || body, options: poll.options.filter(Boolean), multi: poll.multi };
      const { data } = await authClient.post("/feed", payload);
      sfx.pling();
      toast.success("Posted");
      setBody(""); setImages([]); setVideoUrl(""); setPoll({ question: "", options: ["", ""], multi: false });
      setKind("text");
      onPosted?.(data);
    } catch (err) {
      sfx.error();
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="rounded-2xl glass p-5 space-y-3" data-testid="post-composer">
      <div className="flex items-start gap-3 relative">
        <img src={user.avatar} alt="" className="w-10 h-10 rounded-lg border border-white/10 object-cover" />
        <div className="flex-1 relative">
          <textarea
            ref={textRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={
              kind === "question" ? "Ask the community…"
                : kind === "poll" ? "What's your poll question?"
                : kind === "announcement" ? "Share an announcement…"
                : "What's happening on the network? Use @mentions and #hashtags"
            }
            rows={3}
            maxLength={4000}
            className="w-full bg-transparent focus:outline-none resize-none text-white placeholder:text-white/40 text-sm"
            data-testid="post-composer-body"
          />
          <MentionAutocomplete inputRef={textRef} value={body} onInsert={setBody} />
        </div>
      </div>

      {/* Kind selector */}
      <div className="flex gap-1.5 flex-wrap">
        {KIND_OPTIONS.map(({ key, label, icon: Icon }) => (
          <button
            key={key} type="button"
            onClick={() => { setKind(key); sfx.click(); }}
            data-testid={`kind-${key}`}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest border ${
              kind === key ? "border-[#FFBF00] bg-[#FFBF00]/15 text-[#FFBF00]" : "border-white/10 text-white/60 hover:border-white/25"
            }`}
          >
            {Icon && <Icon size={10} />} {label}
          </button>
        ))}
      </div>

      {/* Video URL */}
      {(kind === "video" || kind === "clip") && (
        <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
               placeholder="YouTube / Twitch / any video URL"
               className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FFBF00]/40" />
      )}

      {/* Poll options */}
      {kind === "poll" && (
        <div className="space-y-2">
          {poll.options.map((opt, i) => (
            <input key={`poll-opt-${i}`} value={opt}
                   onChange={(e) => setPoll({ ...poll, options: poll.options.map((o, j) => j === i ? e.target.value : o) })}
                   placeholder={`Option ${i + 1}`}
                   className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FFBF00]/40" />
          ))}
          {poll.options.length < 6 && (
            <button type="button" onClick={() => setPoll({ ...poll, options: [...poll.options, ""] })}
                    className="text-[10px] uppercase tracking-widest text-[#FFBF00] hover:underline">+ Add option</button>
          )}
        </div>
      )}

      {/* Image previews */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {images.map((src, i) => (
            <div key={`preview-${i}-${(src || "").slice(-16)}`} className="relative rounded-md overflow-hidden">
              <img src={src} alt="" className="w-full h-24 object-cover" />
              <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))}
                      className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white hover:bg-red-500">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 pt-2 border-t border-white/10">
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => addImages(e.target.files)} />
        <button type="button" onClick={() => fileRef.current?.click()}
                data-testid="post-composer-image"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 hover:border-[#FFBF00]/40 text-[11px] uppercase tracking-widest text-white/70">
          <ImageIcon size={13} /> Images
        </button>
        <div className="ml-auto text-[10px] text-white/40 font-mono">{body.length}/4000</div>
        <button type="submit" disabled={busy} data-testid="post-composer-submit"
                data-testid="post-composer-submit"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold disabled:opacity-50">
          <Send size={12} /> {busy ? "Posting…" : "Post"}
        </button>
      </div>

      <div className="text-[10px] text-white/30">
        Tip: use <span className="font-mono text-[#FFBF00]/70">#tags</span> and <span className="font-mono text-[#FFBF00]/70">@mentions</span> in your body.
      </div>
    </form>
  );
}
